import * as React from "react";

import Alert from "@mui/material/Alert";
import Backdrop from "@mui/material/Backdrop";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import { datadogRum } from "@datadog/browser-rum";

import {
  CHUNK_LOAD_ERROR_MESSAGE,
  isChunkLoadError
} from "utils/lazy-with-retry.util";
import { reloadPage } from "utils/reload-page.util";

interface DialogLoadErrorBoundaryProps {
  readonly children: React.ReactNode;
  // Identifies the failed dialog in the console log + Datadog error, and in the
  // panel copy — e.g. "email-form", matching the `chunkName` passed to
  // `lazyWithRetry` for the same dialog.
  readonly dialogName: string;
  // Lets a parent that is waiting on the lazy component learn it will never arrive
  // (ENG-2089: consumers suspended on the scribe engine must stop waiting).
  readonly onError?: (error: Error) => void;
}

interface DialogLoadErrorBoundaryState {
  readonly error?: Error;
}

/**
 * ENG-1803 final review (Important #2/#3). Same fix as ScanLoadErrorBoundary
 * (DEV-2806), applied to the three lazily-loaded app-shell dialogs (EmailForm /
 * FaxForm / AppointmentForm). Before this, none of their Suspense boundaries had
 * an error boundary above them short of App.tsx's top-level one, so a chunk-load
 * failure on any of them took down the WHOLE app — worse than before this series,
 * when their code was already bundled and couldn't fail to load.
 *
 * Deliberately narrow: only chunk-load failures are handled here
 * (`isChunkLoadError`). Anything else is a real bug in the dialog and still
 * belongs at the route boundary, where it gets reported rather than dressed up
 * as a network hiccup.
 */
export class DialogLoadErrorBoundary extends React.Component<
  DialogLoadErrorBoundaryProps,
  DialogLoadErrorBoundaryState
> {
  state: DialogLoadErrorBoundaryState = {};

  static getDerivedStateFromError(error: Error): DialogLoadErrorBoundaryState {
    if (!isChunkLoadError(error)) throw error;
    return { error };
  }

  componentDidCatch(error: Error) {
    const { dialogName } = this.props;
    console.error(`${dialogName} chunk failed to load`, error);
    // Keeping the failure inside the dialog also takes it out of
    // RouteErrorBoundary, which reports to Datadog — mirror that here so a
    // fleet-wide stale-asset regression on one of these dialogs doesn't go
    // unnoticed.
    datadogRum.addError(error, { feature: `${dialogName}-chunk-load` });
    this.props.onError?.(error);
  }

  render() {
    if (this.state.error) {
      return (
        <Backdrop
          open
          aria-hidden={false}
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
          <Alert severity="warning" sx={{ maxWidth: 480 }}>
            <Typography variant="body3" fontWeight={700} gutterBottom>
              This could not be loaded
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {CHUNK_LOAD_ERROR_MESSAGE}
            </Typography>
            <Button variant="contained" size="small" onClick={reloadPage}>
              Reload page
            </Button>
          </Alert>
        </Backdrop>
      );
    }
    return this.props.children;
  }
}
